/**
 * HTTP Client
 * Fetch-based HTTP client with interceptors, error handling, and token management
 */

import {
  API_URL,
  REQUEST_TIMEOUT,
  DEFAULT_HEADERS,
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  ENDPOINTS
} from './config';
import { takePrefetched } from './prefetch';

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Get stored auth token
 * @returns {string|null}
 */
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

/**
 * Set auth token
 * @param {string} token
 */
export const setToken = (token) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to save token:', error);
  }
};

/**
 * Remove auth token
 */
export const removeToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove token:', error);
  }
};

/**
 * Get stored refresh token
 * @returns {string|null}
 */
export const getRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

/**
 * Set refresh token
 * @param {string} token
 */
export const setRefreshToken = (token) => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to save refresh token:', error);
  }
};

/**
 * Remove refresh token
 */
export const removeRefreshToken = () => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove refresh token:', error);
  }
};

/**
 * Clear all auth tokens and user data from all auth sources
 */
export const clearAllAuth = () => {
  removeToken();
  removeRefreshToken();
  try {
    localStorage.removeItem('diil_user');
    localStorage.removeItem('aichatbot_token');
    // aichatbot re-auth credentials — clear so a stale phone can't silently
    // re-mint an AI session after logout / session expiry.
    localStorage.removeItem('aichatbot_last_phone');
    localStorage.removeItem('aichatbot_last_country');
    localStorage.removeItem('toki_session');
    localStorage.removeItem('hambi_session');
    localStorage.removeItem('diil_test_access_token');
    // Профайлын кэш (TokiContext) — толгой хэсгийн зургийг шууд харуулахад
    // хэрэглэгддэг. Энд цэвэрлэхгүй бол HambiContext.logout() шиг
    // auth:sessionExpired илгээдэггүй гарц дээр өмнөх хэрэглэгчийн зураг
    // дараагийнх нь дээр түр харагдана.
    localStorage.removeItem('diil_profile_cache');
  } catch (error) {
    console.error('Failed to remove auth data:', error);
  }
};

/**
 * Token refresh state to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Subscribe to token refresh completion
 * @param {Function} callback - Called with new token when refresh completes
 */
const subscribeToTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all subscribers that token refresh completed
 * @param {string|null} newToken - New token or null if refresh failed
 */
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

/**
 * Attempt to refresh the auth token.
 * Single-flight: concurrent callers share one in-flight request — pages that
 * fire many parallel API calls (e.g. the map) used to spawn one doomed
 * refresh per call. A 4xx from the refresh endpoint means the refresh token
 * itself is dead, so it is dropped immediately to stop further attempts.
 * @returns {Promise<string|null>} - New token or null if refresh failed
 */
let refreshInFlight = null;
export const attemptTokenRefresh = () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doTokenRefresh().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
};

const doTokenRefresh = async () => {
  const refreshToken = getRefreshToken();

  // No refresh token available - cannot refresh
  if (!refreshToken) {
    console.warn('No refresh token available for token refresh');
    return null;
  }

  try {
    const url = `${API_URL}${ENDPOINTS.AUTH.REFRESH}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      console.warn('Token refresh failed:', response.status);
      // 4xx = refresh token rejected for good; keep it only on 5xx/network
      if (response.status >= 400 && response.status < 500) {
        removeToken();
        removeRefreshToken();
        window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
      }
      return null;
    }

    const data = await response.json();

    // Store new tokens
    if (data.token || data.accessToken) {
      const newToken = data.token || data.accessToken;
      setToken(newToken);

      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
      }

      return newToken;
    }

    return null;
  } catch (error) {
    console.error('Token refresh error:', error);
    return null;
  }
};

/**
 * Handle 403 Forbidden response
 * 403 = authorization failure (user lacks permission), not authentication failure.
 * Do NOT clear auth — the user may be validly authenticated but just lack access to this resource.
 */
const handle403 = () => {
  window.dispatchEvent(new CustomEvent('auth:forbidden'));
};

/**
 * Handle 401 Unauthorized response
 * Attempts to refresh token and retry the original request
 * @param {string} endpoint - Original request endpoint
 * @param {Object} options - Original request options
 * @returns {Promise<Object|null>} - Retry response or null if refresh failed
 */
const handle401 = async (endpoint, options) => {
  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeToTokenRefresh((newToken) => {
        if (newToken) {
          // Retry with new token
          resolve(request(endpoint, { ...options, _isRetry: true }));
        } else {
          resolve(null);
        }
      });
    });
  }

  isRefreshing = true;

  try {
    const newToken = await attemptTokenRefresh();

    if (newToken) {
      onTokenRefreshed(newToken);
      // Retry original request with new token
      return request(endpoint, { ...options, _isRetry: true });
    } else {
      // Refresh failed - clear JWT tokens but preserve super-app sessions
      // (toki_session / hambi_session have 30-day lifetime and can be used
      // by retryAuth() to silently re-authenticate)
      removeToken();
      removeRefreshToken();
      onTokenRefreshed(null);

      // Dispatch event for app to handle (AuthGuard will try super-app re-auth or show login)
      window.dispatchEvent(new CustomEvent('auth:sessionExpired'));

      return null;
    }
  } finally {
    isRefreshing = false;
  }
};

/**
 * Request interceptors - run before each request
 */
const requestInterceptors = [];

/**
 * Response interceptors - run after each response
 */
const responseInterceptors = [];

/**
 * Add request interceptor
 * @param {Function} interceptor - (config) => config
 */
export const addRequestInterceptor = (interceptor) => {
  requestInterceptors.push(interceptor);
};

/**
 * Add response interceptor
 * @param {Function} interceptor - (response) => response
 */
export const addResponseInterceptor = (interceptor) => {
  responseInterceptors.push(interceptor);
};

/**
 * Apply request interceptors
 * @param {Object} config - Request configuration
 * @returns {Object} - Modified configuration
 */
const applyRequestInterceptors = async (config) => {
  let modifiedConfig = { ...config };
  for (const interceptor of requestInterceptors) {
    modifiedConfig = await interceptor(modifiedConfig);
  }
  return modifiedConfig;
};

/**
 * Apply response interceptors
 * @param {Object} response - Response object
 * @returns {Object} - Modified response
 */
const applyResponseInterceptors = async (response) => {
  let modifiedResponse = response;
  for (const interceptor of responseInterceptors) {
    modifiedResponse = await interceptor(modifiedResponse);
  }
  return modifiedResponse;
};

/**
 * Create fetch with timeout using AbortController
 * Properly cancels the network request on timeout instead of just racing.
 * @param {string} url
 * @param {Object} options
 * @param {number} timeout
 * @returns {Promise<Response>}
 */
export const fetchWithTimeout = (url, options, timeout = REQUEST_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(url, { ...options, signal: controller.signal })
    .then((response) => {
      clearTimeout(timeoutId);
      return response;
    })
    .catch((error) => {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408);
      }
      throw error;
    });
};

/**
 * Reject with a timeout error if the promise hasn't settled in time
 * @param {Promise} promise
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise}
 */
const withTimeout = (promise, timeout) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => reject(new ApiError('Request timeout', 408)), timeout);
  promise.then(
    (value) => { clearTimeout(timeoutId); resolve(value); },
    (error) => { clearTimeout(timeoutId); reject(error); }
  );
});

/**
 * Build full URL
 * @param {string} endpoint
 * @param {Object} params - Query parameters
 * @returns {string}
 */
const buildUrl = (endpoint, params = null) => {
  // If endpoint is already a full URL, use it directly
  if (endpoint.startsWith('http')) {
    return endpoint;
  }

  const url = new URL(`${API_URL}${endpoint}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle arrays - append each value separately for Spring @RequestParam
        if (Array.isArray(value)) {
          value.forEach(v => {
            if (v !== undefined && v !== null) {
              url.searchParams.append(key, String(v));
            }
          });
        } else {
          url.searchParams.append(key, String(value));
        }
      }
    });
  }

  return url.toString();
};

/**
 * Main request function
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>}
 */
const request = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    body = null,
    params = null,
    headers = {},
    timeout = REQUEST_TIMEOUT,
    skipAuth = false,
    _isRetry = false, // Internal flag to prevent infinite retry loops
    ...restOptions
  } = options;

  // Build request configuration
  let config = {
    method,
    headers: {
      // A GET has no body, so Content-Type would only turn each cross-origin call into a
      // preflighted one (an extra OPTIONS round trip); Accept alone keeps it a simple request.
      ...(method === 'GET' ? { Accept: DEFAULT_HEADERS.Accept } : DEFAULT_HEADERS),
      ...headers
    },
    ...restOptions
  };

  // Add auth token if available and not skipped
  if (!skipAuth) {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  // Apply request interceptors
  config = await applyRequestInterceptors(config);

  const url = buildUrl(endpoint, params);

  // A plain unauthenticated GET may already be on its way from public/index.html. Taken only
  // when this request is exactly what that script sent: same URL, no auth, nothing extra.
  const prefetched = method === 'GET' && !config.headers.Authorization
    && Object.keys(headers).length === 0 && requestInterceptors.length === 0
    ? takePrefetched(url)
    : null;

  try {
    const response = prefetched
      ? await withTimeout(prefetched, timeout).catch(() => fetchWithTimeout(url, config, timeout))
      : await fetchWithTimeout(url, config, timeout);

    // Parse response - read as text first, then try JSON parse
    let data;
    const contentType = response.headers.get('content-type');
    const text = await response.text();

    if (contentType && contentType.includes('application/json')) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    } else {
      data = text;
    }

    // Handle HTTP errors
    if (!response.ok) {
      // Handle 401 Unauthorized - attempt token refresh
      if (response.status === 401 && !_isRetry && !skipAuth) {
        const retryResult = await handle401(endpoint, options);
        if (retryResult) {
          return retryResult;
        }
        // If refresh failed, throw the original error
        throw new ApiError('Session expired. Please log in again.', 401, data);
      }

      // Handle 403 Forbidden - clear auth and prompt re-login
      if (response.status === 403 && !skipAuth) {
        handle403();
        throw new ApiError('Access denied. Please log in again.', 403, data);
      }

      const errorMessage = data?.message || data?.error || `HTTP Error: ${response.status}`;
      throw new ApiError(errorMessage, response.status, data);
    }

    // Build standardized response
    let result = {
      success: true,
      status: response.status,
      data,
      headers: response.headers
    };

    // Apply response interceptors
    result = await applyResponseInterceptors(result);

    return result;
  } catch (error) {
    // Handle network errors
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle other errors (network, timeout, etc.)
    throw new ApiError(
      error.message || 'Network error',
      error.status || 0,
      null
    );
  }
};

/**
 * HTTP Methods
 */
export const http = {
  /**
   * GET request
   * @param {string} endpoint
   * @param {Object} params - Query parameters
   * @param {Object} options - Additional options
   */
  get: (endpoint, params = null, options = {}) =>
    request(endpoint, { method: 'GET', params, ...options }),

  /**
   * POST request
   * @param {string} endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional options
   */
  post: (endpoint, body = null, options = {}) =>
    request(endpoint, { method: 'POST', body, ...options }),

  /**
   * PUT request
   * @param {string} endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional options
   */
  put: (endpoint, body = null, options = {}) =>
    request(endpoint, { method: 'PUT', body, ...options }),

  /**
   * PATCH request
   * @param {string} endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional options
   */
  patch: (endpoint, body = null, options = {}) =>
    request(endpoint, { method: 'PATCH', body, ...options }),

  /**
   * DELETE request
   * @param {string} endpoint
   * @param {Object} options - Additional options
   */
  delete: (endpoint, options = {}) =>
    request(endpoint, { method: 'DELETE', ...options }),

  /**
   * POST FormData request (for file uploads)
   * @param {string} endpoint
   * @param {FormData} formData - FormData object
   * @param {Object} options - Additional options
   */
  postFormData: async (endpoint, formData, options = {}) => {
    const { _isRetry = false, ...restOptions } = options;
    const token = getToken();
    const url = buildUrl(endpoint);

    const config = {
      method: 'POST',
      headers: {},
      body: formData,
      ...restOptions
    };

    // Add auth token
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Note: Don't set Content-Type for FormData - browser will set it with boundary
    try {
      const response = await fetchWithTimeout(url, config, REQUEST_TIMEOUT);

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Handle 401 Unauthorized - attempt token refresh
        if (response.status === 401 && !_isRetry) {
          const newToken = await attemptTokenRefresh();
          if (newToken) {
            // Retry with new token
            return http.postFormData(endpoint, formData, { ...options, _isRetry: true });
          }
          // Refresh failed - clear JWT tokens but preserve super-app sessions
          removeToken();
          removeRefreshToken();
          window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
          throw new ApiError('Session expired. Please log in again.', 401, data);
        }

        // Handle 403 Forbidden - clear auth and prompt re-login
        if (response.status === 403) {
          handle403();
          throw new ApiError('Access denied. Please log in again.', 403, data);
        }

        const errorMessage = data?.message || data?.error || `HTTP Error: ${response.status}`;
        throw new ApiError(errorMessage, response.status, data);
      }

      return { success: true, status: response.status, data };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error.message || 'Network error', error.status || 0, null);
    }
  }
};

// Default export
export default http;
